import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import Alert from './components/Alert/Alert';
import Navbar from './components/Navbar/Navbar';
import AppRoutes from './routes/routes';
import { SocketProvider } from './context/SocketContext';
import { FoodProvider } from './context/FoodContext';
import { MessageProvider } from './context/MessageContext';
import axios from 'axios';
import { buildApiUrl } from './config/api.config';


  
function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

function App() {
  const [alert, setAlert] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const [maintenanceMode, setMaintenanceMode] = useState(false); // Maintenance State
  const [licenseExpired, setLicenseExpired] = useState(false); // License State
  const [expiryDate, setExpiryDate] = useState(null);
  const location = useLocation();

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 1500);
  };

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return { success: false, status: 401 };

      const response = await fetch(buildApiUrl("/users/getuser"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "auth-token": token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        return { success: false, status: response.status };
      }
    } catch (error) {
      console.error("Error fetching user details:", error.message);
      return { success: false, status: 500 };
    }
  };

  useEffect(() => {
    const syncFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const restaurantId = urlParams.get('restaurantId');

      if (token) {
        localStorage.setItem('token', token);
      }
      if (restaurantId) {
        localStorage.setItem('currentRestaurantId', restaurantId);
      }

      // Clean up URL after sync if tokens was found
      if (token || restaurantId) {
         const newUrl = window.location.pathname + window.location.hash;
         window.history.replaceState({}, document.title, newUrl);
      }
    };

    syncFromUrl();

    const getUserDetails = async () => {
      const result = await fetchUserDetails();
      
      if (result && result.success) {
        const userData = result.data;
        setUserDetails(userData);
        localStorage.setItem("userDetails", JSON.stringify(userData));
        
        // Check for License Expiry
        if (userData && userData.restaurantId && userData.restaurantId.licenseExpiryDate) {
           const exp = new Date(userData.restaurantId.licenseExpiryDate);
           setExpiryDate(exp);
           if (exp < new Date()) {
              setLicenseExpired(true);
           }
        }
      } else if (result && (result.status === 401 || result.status === 403 || result.status === 404)) {
        // Only remove token if explicitly unauthorized, forbidden or not found
        if (localStorage.getItem("token")) {
          localStorage.removeItem("token");
          localStorage.removeItem("userDetails");
          localStorage.removeItem("currentRestaurantId");
          // Redirect to login after clearing invalid credentials
          window.location.href = "/login";
          return;
        }
      }

      setLoading(false);
    };

    getUserDetails();
  }, [location.search]); // Run when search params change

  // Global Axios Interceptor for Session Enforcement
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 403 && error.response.data.code === 'RESTAURANT_INACTIVE') {
          console.log("🚫 Restaurant inactive. Logging out...");
          localStorage.removeItem("token");
          localStorage.removeItem("userDetails");
          localStorage.removeItem("currentRestaurantId");
          window.location.href = "/login?error=Restaurant account is inactive. Please contact support.";
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Maintenance Check
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(buildApiUrl("/platform/status"));
        const data = await res.json();
        if (data.success && data.maintenanceMode) {
          setMaintenanceMode(true);
        } else {
          setMaintenanceMode(false);
        }
      } catch (err) {
        console.error("Status check failed", err);
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  if (maintenanceMode) {
    return (
      <div style={{ height: '100vh', width: '100vw', background: '#0a0a0c', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999, position: 'fixed', top: 0, left: 0 }}>
        <div style={{ padding: '3rem', background: '#111214', border: '1px solid rgba(255, 65, 53, 0.2)', borderRadius: '24px', textAlign: 'center', maxWidth: '500px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1.5rem', color: '#ff4135' }}>
               <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>System Under Maintenance</h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', lineHeight: '1.6', marginBottom: '2rem' }}>
                The TastyFlow Platform is currently undergoing scheduled maintenance to improve performance and security. All restaurant nodes are temporarily locked.
            </p>
            <div style={{ padding: '1rem', background: 'rgba(255, 65, 53, 0.05)', borderRadius: '12px', border: '1px solid rgba(255, 65, 53, 0.1)', fontSize: '0.9rem', color: '#ff4135', fontWeight: 'bold' }}>
                Please check back shortly.
            </div>
            <button 
                onClick={() => window.location.reload()} 
                style={{ marginTop: '2rem', background: 'white', color: 'black', border: 'none', padding: '1rem 2rem', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}
            >
                Check Status
            </button>
        </div>
      </div>
    );
  }

  if (licenseExpired) {
    return (
      <div style={{ height: '100vh', width: '100vw', background: '#0a0a0c', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999, position: 'fixed', top: 0, left: 0 }}>
        <div style={{ padding: '3rem', background: '#111214', border: '1px solid rgba(255, 65, 53, 0.2)', borderRadius: '24px', textAlign: 'center', maxWidth: '500px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1.5rem', color: '#ff4135' }}>
               <i className="fa-solid fa-lock"></i>
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>Subscription Expired</h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', lineHeight: '1.6', marginBottom: '2rem' }}>
                Your restaurant's license expired on <strong style={{color: 'white'}}>{expiryDate ? expiryDate.toLocaleDateString() : 'N/A'}</strong>. Access to the Provider Portal has been suspended.
            </p>
            <div style={{ padding: '1rem', background: 'rgba(255, 65, 53, 0.05)', borderRadius: '12px', border: '1px solid rgba(255, 65, 53, 0.1)', fontSize: '0.9rem', color: '#ff4135', fontWeight: 'bold' }}>
                Contact Platform Admin to Renew
            </div>
            <button 
                onClick={() => window.location.reload()} 
                style={{ marginTop: '2rem', background: 'var(--tf-primary)', color: 'white', border: 'none', padding: '1rem 2rem', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}
            >
                Refresh License Status
            </button>
        </div>
      </div>
    );
  }

  const hideNavbarRoutes = ['/portals', '/login', '/signup', '/forgot-password', '/reset-password', '/complete-profile', '/select-restaurant'];
  const shouldShowNavbar = !hideNavbarRoutes.some(route => location.pathname.startsWith(route)) && 
                          !location.pathname.startsWith('/admin') && 
                          !location.pathname.startsWith('/master-panel');

  return (
    <SocketProvider>
      <FoodProvider>
        <MessageProvider>
        {shouldShowNavbar && <Navbar showAlert={showAlert} userDetails={userDetails} />}
        <Alert alert={alert} />
        <AppRoutes showAlert={showAlert} />
        </MessageProvider>
      </FoodProvider>
    </SocketProvider>
  );
}

export default AppWrapper;  